import {
  ActionsCell,
  useActionsCellColumnBase,
  useRenderActionsCellColumn,
} from '../ActionsCell';
import { FoldersAndItemsCollectionsState } from '../../state/FoldersAndItemsCollectionsState';
import { FiltersMap } from '@wix/bex-core';
import { TableColumn as TableColumnWSR } from '@wix/design-system/dist/types/Table';
import { useMemo } from 'react';
import { isFolderKeyedItem, isItemKeyedItem, RowKeyedItem } from '../../state';

export interface UseOrFolderActionsCellColumnParams<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
> {
  state: FoldersAndItemsCollectionsState<T1, F1, T2, F2>;
  actionsCell?: ActionsCell<T1, F1>;
  actionsCellWidth?: string | number;
  actionsCellProps?: Partial<Omit<TableColumnWSR, 'render' | 'style'>>;
  folderActionsCell?: ActionsCell<T2, F2>;
}

export function useOrFolderActionsCellColumn<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
>(
  params: UseOrFolderActionsCellColumnParams<T1, F1, T2, F2>,
): TableColumnWSR<RowKeyedItem<T1, T2>> | null {
  const {
    state: { items, folders },
    actionsCell,
    folderActionsCell,
    actionsCellProps,
    actionsCellWidth,
  } = params;

  const baseColumn = useActionsCellColumnBase({
    actionsCellProps,
    actionsCellWidth,
  });

  const renderItem = useRenderActionsCellColumn({
    actionsCell,
    sticky: true,
    collection: items,
  });

  const renderFolder = useRenderActionsCellColumn({
    actionsCell: folderActionsCell,
    sticky: true,
    collection: folders,
  });

  return useMemo(() => {
    if (renderItem == null && renderFolder == null) {
      return null;
    }

    return {
      ...baseColumn,
      render: (keyedItem, rowNum) => {
        if (isItemKeyedItem(keyedItem)) {
          return renderItem?.(keyedItem.item.originalKeyedItem, rowNum);
        }

        if (isFolderKeyedItem(keyedItem)) {
          return renderFolder?.(keyedItem.item.originalKeyedItem, rowNum);
        }

        return null;
      },
    };
  }, [baseColumn, renderItem, renderFolder]);
}
